import type { Linework } from "building-types/annotation/annotationTypes";
import { AnnotationFillType, AnnotationLineType } from "building-types/annotation/annotationTypesEnum";
import type { PolyLineOutputOptions } from "building-types/views/Primitive2DOutput";
import { PDF_DPI } from "common/constants/pdfSettings";
import type { SheetPoints } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { numMul, percentNew } from "common/utils/nominalNumbers";
import { hatchProceduralMaterialNew } from "geom/hatchTypes";
import { polyLine2Close, polyLine2IsClosed } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { LW_EXTRA_THIN, LW_THIN } from "../plans/constants";
import type { AnnotationFillPattern } from "../utils/annotation";
import { annotationFillPatternByType, lineworkDashFormatByType, polyLineFromShapeOffset } from "../utils/annotation";

// Converts dash lengths in inches to pdf units, normalized around a 1/4" = 1' scale.
const dashLineworkScale = PDF_DPI * (0.25 / 12);

export interface AnnotationLineworkOutput {
  readonly lines: Omit<PolyLineOutputOptions, "layout">[];
  readonly offsetLines: Omit<PolyLineOutputOptions, "layout">[];
  readonly fills: Omit<PolyLineOutputOptions, "layout">[];
}

// TODO: convert annotation linework to directly use width values in instead of a multiplier
function lineworkLinewidth(dashed: boolean, lineweight: number): SheetPoints {
  return numMul(dashed ? LW_THIN : LW_EXTRA_THIN, lineweight);
}

export function makeStyledAnnotationLinework(linework: Linework, color?: string): AnnotationLineworkOutput {
  const output: AnnotationLineworkOutput = { lines: [], offsetLines: [], fills: [] };

  if (!linework.path) {
    return output;
  }

  function fillOutputForShape(polyLine: PolyLine2, fillType: AnnotationFillType) {
    const fillPattern: AnnotationFillPattern = annotationFillPatternByType[fillType];

    if (fillPattern.fillStyle === "Hatch") {
      const { lineDirection, lineSpacing, crossHatch, hatchMaterialId } = fillPattern;
      output.fills.push({
        paths: [polyLine],
        hatchStrokeColor: "#c0c0c0",
        linewidth: LW_THIN,
        hatch: true,
        hatchStroke: false,
        hatchMaterial: hatchProceduralMaterialNew(hatchMaterialId),
        hatchCrossHatch: crossHatch,
        hatchLineSpacing: lineSpacing,
        hatchDirection: lineDirection,
      });
    } else if (fillPattern.fillStyle === "Solid") {
      const { color } = fillPattern;
      output.fills.push({
        paths: [polyLine],
        linewidth: LW_THIN,
        strokeOpacity: percentNew(0),
        fill: true,
        fillColor: color,
        fillOpacity: percentNew(1),
      });
    }
  }

  if (linework.lineType !== AnnotationLineType.NONE) {
    const dashed = linework.lineType !== AnnotationLineType.CONTINUOUS;

    const dashLength =
      lineworkDashFormatByType[linework.lineType]?.dashLength?.map((x) => numMul(x, dashLineworkScale)) ?? [];

    output.lines.push({
      paths: [linework.path],
      linewidth: lineworkLinewidth(dashed, linework.lineWeight),
      dash: dashed,
      dashLength,
      strokeColor: color,
    });
  }

  if (linework.fillType) {
    const closedPath = polyLine2Close(linework.path);
    fillOutputForShape(closedPath, linework.fillType);
  }

  if (linework.offset) {
    const offsetPolyLine = polyLineFromShapeOffset(linework);
    assertIsNotUndefined(offsetPolyLine);

    if (linework.offsetLineType !== AnnotationLineType.NONE) {
      const dashed = linework.offsetLineType !== AnnotationLineType.CONTINUOUS;

      const dashLength =
        lineworkDashFormatByType[linework.offsetLineType]?.dashLength?.map((x) => numMul(x, dashLineworkScale)) ?? [];

      output.offsetLines.push({
        paths: [offsetPolyLine],
        linewidth: lineworkLinewidth(dashed, linework.lineWeight),
        dash: dashed,
        dashLength,
        strokeColor: color,
      });
    }

    if (linework.offsetFillType !== AnnotationFillType.NONE && polyLine2IsClosed(offsetPolyLine)) {
      fillOutputForShape(offsetPolyLine, linework.offsetFillType);
    }
  }

  return output;
}
